import React from 'react';
import { GameStateProvider, useGameState } from './GameStateContext';
import { PlayerProvider, usePlayer } from './PlayerContext';
import { GameObjectsProvider, useGameObjects } from './GameObjectsContext';
import { AudioProvider, useAudio } from './AudioContext';
import { GraphicsProvider, useGraphics } from './GraphicsContext';
import { AnimationProvider, useAnimation } from './AnimationContext';
import { ConnectionProvider, useConnection } from './ConnectionContext';

// Internal component that provides the ConnectionContext after other contexts are available
const ConnectionProviderWrapper = ({ children, roomId, nickname }) => {
  const gameStateActions = useGameState();
  const playerActions = usePlayer();
  const gameObjectsActions = useGameObjects();
  const animationActions = useAnimation();
  const audioActions = useAudio();

  return (
    <ConnectionProvider
      roomId={roomId}
      nickname={nickname}
      gameStateActions={gameStateActions}
      playerActions={playerActions}
      gameObjectsActions={gameObjectsActions}
      animationActions={animationActions}
      audioActions={audioActions}
    >
      {children}
    </ConnectionProvider>
  );
};

// Main context provider that wraps all contexts in the correct order
export const BombermanContextProvider = ({ children, roomId, nickname }) => {
  return (
    <GameStateProvider>
      <PlayerProvider roomId={roomId}>
        <GameObjectsProvider>
          <AudioProvider>
            <GraphicsProvider>
              <AnimationProvider>
                <ConnectionProviderWrapper roomId={roomId} nickname={nickname}>
                  {children}
                </ConnectionProviderWrapper>
              </AnimationProvider>
            </GraphicsProvider>
          </AudioProvider>
        </GameObjectsProvider>
      </PlayerProvider>
    </GameStateProvider>
  );
};

// Combined hook for accessing all contexts (optional convenience hook)
export const useBombermanContext = () => {
  const gameState = useGameState();
  const player = usePlayer();
  const gameObjects = useGameObjects();
  const audio = useAudio();
  const graphics = useGraphics();
  const animation = useAnimation();
  const connection = useConnection();

  return React.useMemo(() => ({
    gameState,
    player,
    gameObjects,
    audio,
    graphics,
    animation,
    connection
  }), [gameState, player, gameObjects, audio, graphics, animation, connection]);
};

// Individual context exports for direct usage
export {
  useGameState,
  usePlayer,
  useGameObjects,
  useAudio,
  useGraphics,
  useAnimation,
  useConnection
};

export default BombermanContextProvider;